import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// --- SUB-MODAL DE AUTENTICACIÓN ---
const SecurityModal = ({ isOpen, onClose, onConfirm, label }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  // Enfocar input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
        setTimeout(() => inputRef.current.focus(), 50);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === '1234') { // CLAVE HARDCODEADA DE EJEMPLO
        onConfirm();
        setPin('');
        setError(false);
        onClose();
    } else {
        setError(true);
        setPin('');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden transform transition-all scale-100 animate-fade-in border border-gray-200">
            
            {/* Header con candado */}
            <div className="bg-gray-50 p-4 flex flex-col items-center justify-center border-b border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                </div>
                <h3 className="text-gray-900 font-bold text-lg">Seguridad</h3>
                <p className="text-xs text-gray-500 text-center mt-1 px-2">
                    Ingrese clave para ver <br/>
                    <span className="font-semibold text-blue-600">{label}</span>
                </p>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="p-5">
                <input 
                    ref={inputRef}
                    type="password" 
                    value={pin}
                    onChange={(e) => { setPin(e.target.value); setError(false); }}
                    placeholder="Ingrese PIN"
                    className={`w-full text-center text-2xl tracking-widest font-bold border-b-2 outline-none py-2 transition-colors ${error ? 'border-red-500 text-red-600 placeholder-red-300' : 'border-gray-300 focus:border-blue-500 text-gray-800'}`}
                    maxLength={4}
                />
                {error && <p className="text-xs text-red-500 text-center mt-2 font-medium">Clave incorrecta</p>}
                
                <div className="mt-6 flex gap-3">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="flex-1 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        className="flex-1 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md transition-colors"
                    >
                        Desbloquear
                    </button>
                </div>
            </form>
        </div>
    </div>,
    document.body
  );
};


// --- COMPONENTE PRINCIPAL ---
const SensitiveDataRow = ({ label, value, type = 'text' }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const handleToggle = () => {
    if (isRevealed) {
      setIsRevealed(false);
    } else {
      setShowAuth(true);
    }
  };

  const getMaskedValue = () => {
    if (!value) return 'Sin datos';
    const visibleDigits = 4;
    if (value.length <= visibleDigits) return '••••';
    return `•••• •••• •••• ${value.slice(-visibleDigits)}`;
  };

  return (
    <>
        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors group">
            <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
                <p className={`text-sm font-medium ${isRevealed ? 'text-blue-700 font-mono' : 'text-gray-500'}`}>
                    {isRevealed ? value : getMaskedValue()}
                </p>
            </div>
            <button 
                onClick={handleToggle}
                type="button" // Importante para no disparar submits accidentales
                className={`p-2 rounded-full transition-all ${isRevealed ? 'text-blue-600 bg-blue-50' : 'text-gray-400 bg-gray-50 hover:bg-gray-100 hover:text-gray-600'}`}
                title={isRevealed ? "Ocultar información" : "Mostrar información completa"}
            >
                {isRevealed ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path></svg>
                ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                )}
            </button>
        </div>

        <SecurityModal 
            isOpen={showAuth} 
            onClose={() => setShowAuth(false)} 
            onConfirm={() => setIsRevealed(true)}
            label={label}
        />
    </>
  );
};

export default SensitiveDataRow;